import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { CANONICAL_DOMAIN, getCanonicalUrl, getHreflangTags, getLocalizedEquivalentUrl } from '../lib/routes';
import { useLanguage, PageMetadataKey } from '../context/LanguageContext';
import { safeTruncate, fitDescription, getPageOgImage } from '../lib/seoEngine';

export interface SEOHeadProps {
  pageKey?: PageMetadataKey;
  pageType?:
    | 'home'
    | 'national_land'
    | 'city_land'
    | 'city_properties'
    | 'city_rent'
    | 'quarter_land'
    | 'guide_buying'
    | 'guide_titre_foncier'
    | 'guide_scams'
    | 'custom';
  title?: string;
  description?: string;
  canonicalPath?: string;
  hreflangEn?: string;
  hreflangFr?: string;
  ogImage?: string;
  ogImageAlt?: string;
  ogType?: 'website' | 'article' | 'product';
  noIndex?: boolean;
  breadcrumbs?: Array<{ name: string; url?: string }>;
  jsonLd?: Record<string, any> | Array<Record<string, any>>;
  geoCoords?: [number, number];
  geoPlacename?: string;
  geoRegion?: string;
  citySlug?: string;
  categoryId?: string;
  guideId?: string;
}

export const SEOHead: React.FC<SEOHeadProps> = ({
  pageKey,
  title,
  description,
  canonicalPath,
  hreflangEn,
  hreflangFr,
  ogImage,
  ogImageAlt,
  ogType = 'website',
  noIndex = false,
  breadcrumbs,
  jsonLd,
  geoCoords,
  geoPlacename,
  geoRegion,
  citySlug,
  categoryId,
  guideId,
}) => {
  const location = useLocation();
  const { language, t } = useLanguage();

  useEffect(() => {
    // Sync document html lang attribute
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.lang = language || 'en';
    }

    const pageMeta = pageKey ? t.metadata?.[pageKey] : undefined;

    // 1. Page Title (Strictly <= 60 characters, keyword-first with CTA)
    const defaultTitle = language === 'fr'
      ? 'Terrains & Biens Immobiliers au Cameroun – Annonces'
      : 'Land & Property for Sale in Cameroon – Free Ads';
    let candidateTitle = title?.trim() || pageMeta?.title || defaultTitle;
    const effectiveTitle = safeTruncate(candidateTitle, 60);
    document.title = effectiveTitle;

    // Helper to set or create meta tag
    const setMeta = (nameOrProp: string, value: string, isName = false) => {
      const attr = isName ? 'name' : 'property';
      let tag = document.querySelector(`meta[${attr}="${nameOrProp}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, nameOrProp);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', value);
    };

    // 2. Meta Description (Strictly 140–160 characters, keyword-first with CTA)
    const defaultDesc = language === 'fr'
      ? 'Achetez ou vendez terrains, maisons et appartements au Cameroun. Annonces à Douala, Yaoundé, Kribi et Buea. Déposez votre annonce gratuite en 2 min.'
      : 'Buy or sell land, houses and apartments in Cameroon. Browse plots in Douala, Yaoundé, Kribi and Buea. Post your property ad free in 2 minutes.';
    const candidateDesc = description?.trim() || pageMeta?.description || defaultDesc;
    const effectiveDesc = fitDescription(candidateDesc, language);
    setMeta('description', effectiveDesc, true);

    // 2b. Remove Global Meta Keywords (Task Requirement 1)
    const keywordsTag = document.querySelector('meta[name="keywords"]');
    if (keywordsTag) {
      keywordsTag.remove();
    }

    // 2c. Content-Language HTTP-Equiv
    let contentLangMeta = document.querySelector<HTMLMetaElement>('meta[http-equiv="content-language"]');
    if (!contentLangMeta) {
      contentLangMeta = document.createElement('meta');
      contentLangMeta.setAttribute('http-equiv', 'content-language');
      document.head.appendChild(contentLangMeta);
    }
    contentLangMeta.setAttribute('content', language === 'fr' ? 'fr-CM' : 'en-CM');

    // 2d. Per-City Geo-Targeting (Task Requirement 2: Keep only where accurate per-city coordinates exist)
    if (geoCoords && typeof geoCoords[0] === 'number' && typeof geoCoords[1] === 'number') {
      setMeta('geo.region', geoRegion || 'CM', true);
      setMeta('geo.placename', geoPlacename || 'Cameroon', true);
      setMeta('geo.position', `${geoCoords[0]};${geoCoords[1]}`, true);
      setMeta('ICBM', `${geoCoords[0]}, ${geoCoords[1]}`, true);
    } else {
      const geoPos = document.querySelector('meta[name="geo.position"]');
      if (geoPos) geoPos.remove();
      const icbm = document.querySelector('meta[name="ICBM"]');
      if (icbm) icbm.remove();
      const geoPlace = document.querySelector('meta[name="geo.placename"]');
      if (geoPlace) geoPlace.remove();
      const geoReg = document.querySelector('meta[name="geo.region"]');
      if (geoReg) geoReg.remove();
    }

    // 3. Robots Meta Directive
    if (noIndex) {
      setMeta('robots', 'noindex, follow', true);
    } else {
      setMeta('robots', 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1', true);
    }

    // 4. Strict Self-Referential Canonical Link & Multilingual Hreflang Annotations (Clean URL without Query Params)
    // Derive active subfolder language directly from location.pathname so /fr/... and /en/... never mismatch
    const currentPathname = location.pathname || '/';
    const hasExplicitSubfolder =
      currentPathname === '/fr' ||
      currentPathname.startsWith('/fr/') ||
      currentPathname === '/en' ||
      currentPathname.startsWith('/en/');
    const pathLanguage: 'en' | 'fr' =
      currentPathname === '/fr' || currentPathname.startsWith('/fr/')
        ? 'fr'
        : currentPathname === '/en' || currentPathname.startsWith('/en/')
        ? 'en'
        : language;

    const rawTarget = canonicalPath || currentPathname;
    const cleanRawTarget = rawTarget.split('?')[0].split('#')[0];
    const targetPath = hasExplicitSubfolder
      ? getLocalizedEquivalentUrl(cleanRawTarget, pathLanguage)
      : cleanRawTarget;
    const effectiveCanonicalUrl = getCanonicalUrl(targetPath, pathLanguage, false);
    let canonicalLink = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', effectiveCanonicalUrl);

    // Compute localized alternate URLs for Google International SEO (ISO 639-1: fr, en; ISO 639-1 + ISO 3166-1: fr-cm, en-cm; x-default -> French)
    const computedHreflangs = getHreflangTags(targetPath);
    const finalEnUrl = hreflangEn
      ? (hreflangEn.startsWith('http') ? hreflangEn : `${CANONICAL_DOMAIN}${hreflangEn}`)
      : computedHreflangs.en;
    const finalFrUrl = hreflangFr
      ? (hreflangFr.startsWith('http') ? hreflangFr : `${CANONICAL_DOMAIN}${hreflangFr}`)
      : computedHreflangs.fr;

    const setHreflang = (hreflang: string, href: string) => {
      let link = document.querySelector<HTMLLinkElement>(`link[rel="alternate"][hreflang="${hreflang}"]`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', hreflang);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    setHreflang('fr', finalFrUrl);
    setHreflang('en', finalEnUrl);
    setHreflang('fr-cm', finalFrUrl);
    setHreflang('en-cm', finalEnUrl);
    setHreflang('x-default', finalFrUrl);

    // 5. Open Graph & Social Media Tags with dedicated OG Image & Alt Text
    const resolvedOg = ogImage
      ? { url: ogImage, alt: ogImageAlt || effectiveTitle }
      : getPageOgImage({ citySlug, categoryId, guideId });

    setMeta('og:title', effectiveTitle);
    setMeta('og:description', effectiveDesc);
    setMeta('og:url', effectiveCanonicalUrl);
    setMeta('og:image', resolvedOg.url);
    setMeta('og:image:secure_url', resolvedOg.url);
    setMeta('og:image:width', '1200');
    setMeta('og:image:height', '630');
    setMeta('og:image:alt', resolvedOg.alt);
    setMeta('og:type', ogType);
    setMeta('og:site_name', 'Cameroon Land Market');
    setMeta('og:locale', language === 'fr' ? 'fr_CM' : 'en_CM');
    setMeta('og:locale:alternate', language === 'fr' ? 'en_CM' : 'fr_CM');

    setMeta('twitter:title', effectiveTitle, true);
    setMeta('twitter:description', effectiveDesc, true);
    setMeta('twitter:image', resolvedOg.url, true);
    setMeta('twitter:card', 'summary_large_image', true);

    // 6. Schema.org Structured Data with inLanguage
    const scriptId = 'clm-page-schema';
    let scriptTag = document.getElementById(scriptId) as HTMLScriptElement | null;
    let finalJsonLd = jsonLd;
    if (breadcrumbs && breadcrumbs.length > 0) {
      const breadcrumbNode = {
        '@type': 'BreadcrumbList',
        'itemListElement': breadcrumbs.map((b, idx) => ({
          '@type': 'ListItem',
          'position': idx + 1,
          'name': b.name,
          'item': b.url ? (b.url.startsWith('http') ? b.url : `${CANONICAL_DOMAIN}${b.url}`) : undefined,
        })),
      };
      if (!finalJsonLd) {
        finalJsonLd = breadcrumbNode;
      } else if (Array.isArray(finalJsonLd)) {
        finalJsonLd = [...finalJsonLd, breadcrumbNode];
      } else {
        finalJsonLd = [finalJsonLd, breadcrumbNode];
      }
    }

    if (finalJsonLd) {
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.id = scriptId;
        scriptTag.type = 'application/ld+json';
        document.head.appendChild(scriptTag);
      }
      const langTag = language === 'fr' ? 'fr-CM' : 'en-CM';
      const enrichedJsonLd = Array.isArray(finalJsonLd)
        ? {
            '@context': 'https://schema.org',
            '@graph': finalJsonLd.map((node) => ({
              inLanguage: langTag,
              ...node,
            })),
          }
        : {
            '@context': 'https://schema.org',
            inLanguage: langTag,
            ...finalJsonLd,
          };
      scriptTag.textContent = JSON.stringify(enrichedJsonLd);
    } else if (scriptTag) {
      scriptTag.remove();
    }
  }, [
    title,
    description,
    pageKey,
    canonicalPath,
    hreflangEn,
    hreflangFr,
    ogImage,
    ogImageAlt,
    ogType,
    noIndex,
    breadcrumbs,
    jsonLd,
    geoCoords,
    geoPlacename,
    geoRegion,
    citySlug,
    categoryId,
    guideId,
    location.pathname,
    language,
    t,
  ]);

  return null;
};
